import {
  AssignedProductSave,
  PayerType,
  PaymentFrequency,
  PaymentFrequencyType,
  Product,
  YearlyProductAssignments,
} from '@schooly/api';
import { newDateTimezoneOffset } from '@schooly/utils/date';
import { isAfter } from 'date-fns';
import Decimal from 'decimal.js';

import { AssignedProductUpdate } from './StudentProductsModalContent';

export const getDiscountedPrice = (price: number, discountPercent: number) => {
  const decimalPrice = new Decimal(price);
  const decimalDiscountPercent = new Decimal(discountPercent);

  const discountAmount = decimalPrice.mul(decimalDiscountPercent).div(100);
  const discountedPrice = decimalPrice.minus(discountAmount);

  return discountedPrice.toDecimalPlaces(2, Decimal.ROUND_HALF_EVEN).toNumber();
};

export const convertProductToAssignedProductUpdate = (product: Product): AssignedProductUpdate => {
  const type = product.types[0];
  const variant = type.variants[0];
  const frequencyId = variant.prices[0].frequency_id;

  return {
    id: product.id,
    payer_type: PayerType.Default,
    obligatory: product.obligatory,
    product_type: product.type,
    name: product.name,
    isExistingAssignment: false,
    variant: {
      id: variant.id,
      price: variant.prices[0].price,
      type_name: type.name,
      currency: type.billing_connection.legal_entity_currency,
      frequency_id: frequencyId,
    },
    available_variants: product.types.flatMap((type) =>
      type.variants.map((variant) => ({
        type_id: type.id,
        type_name: type.name,
        id: variant.id,
        half_day: variant.half_day,
        prices: variant.prices,
        currency: type.billing_connection.legal_entity_currency,
      })),
    ),
  };
};

export const convertFormDataToAssignedProductSave = (
  productAssignment: AssignedProductUpdate,
): AssignedProductSave => {
  const { id, variant, payer_type, discount_percent } = productAssignment;

  return {
    id,
    variant: {
      type_name: variant.type_name,
      id: variant.id,
      frequency_id: variant.frequency_id,
    },
    payer_type,
    discount_percent: discount_percent || 0,
  };
};

export const convertProductAssignmentsToForm = (
  productAssignments: YearlyProductAssignments,
  overrideCompanyId?: string,
) => {
  const products = productAssignments.products.map((p) => ({
    ...p,
    discount_percent: p.discount_percent || undefined,
    isExistingAssignment: true,
  }));

  return {
    yearId: productAssignments.year_id,
    defaultPayerId: productAssignments.default_payer.relation_id,
    companyPayerId: overrideCompanyId ?? productAssignments.company_payer?.id,
    products,
  };
};

type CanEditFrequencyProps = {
  product: AssignedProductUpdate;
  initFrequencyId: string;
  frequencies: PaymentFrequency[];
};

//Based on TR-6701 and TR-9258 frequency selection is temporary blocked for existing assignments with invoices
export const canEditFrequencyInProductAssignment = ({
  product,
  initFrequencyId,
  frequencies,
}: CanEditFrequencyProps) => {
  //can edit new product assignment
  if (!product.isExistingAssignment) return true;

  //can edit if new frequency selected
  if (initFrequencyId !== product.variant.frequency_id) {
    return true;
  }

  const currentFrequency = frequencies.find((f) => f.id === product.variant.frequency_id);

  if (!currentFrequency || currentFrequency.type === PaymentFrequencyType.OneOff) {
    return false;
  }

  const today = new Date();

  //can edit if all generation dates of frequency are in the future
  return currentFrequency.billing_periods.every((p) =>
    isAfter(newDateTimezoneOffset(p.generation_date), today),
  );
};
